import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Datagrid,
  FunctionField,
  TextField as RaTextField,
  useNotify,
  useTranslate,
} from 'react-admin'
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs'
import { Iconify } from 'src/components/iconify'
import { DashboardContent } from 'src/layouts/dashboard'
import { PermissionGuard } from 'src/routes/components/permission-guard'
import { PermissionButton } from 'src/routes/components/permission-button'
import ErrorPage403 from 'src/pages/error/403'
import * as userRoleService from '../../services/userRoleService'

type ListRow = userRoleService.UserRoleListUser & { id: string }

function buildListRequest(
  email: string,
  wallet: string
): userRoleService.UserRoleListRequest | null {
  const e = email.trim()
  const w = wallet.trim()
  if (!e && !w) return null
  if (e && w) return null
  if (e) return { email: e }
  return { wallet_address: w }
}

function rowsFromResponse(
  data: Record<string, unknown> | undefined,
  inputs: { userId: string; email: string; wallet: string }
): ListRow[] {
  const normalized = userRoleService.normalizeUserRoleListResponse(data, inputs);
  const base =
    normalized.length > 0
      ? normalized
      : data?.roles !== undefined && Array.isArray(data.roles)
        ? [
            {
              user_id: inputs.userId.trim() || undefined,
              email: inputs.email.trim() || undefined,
              wallet_address: inputs.wallet.trim() || undefined,
              roles: data.roles as string[],
            },
          ]
        : []

  return base.map((row, i) => ({
    ...row,
    id: row.user_id || row.email || row.wallet_address || `row-${i}`,
  }))
}

function identifierFromRow(row: userRoleService.UserRoleListUser): userRoleService.UserRoleIdentifier | null {
  if (row.email?.trim()) return { email: row.email.trim() }
  if (row.wallet_address?.trim()) return { wallet_address: row.wallet_address.trim() }
  if (row.user_id?.trim()) return { user_id: row.user_id.trim() };
  return null
}

const View = () => {
  const t = useTranslate()
  const notify = useNotify()

  const [userIdInput, setUserIdInput] = useState('')
  const [emailInput, setEmailInput] = useState('')
  const [walletInput, setWalletInput] = useState('')

  const [rows, setRows] = useState<ListRow[]>([])
  const [availableRoles, setAvailableRoles] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const [grantOpen, setGrantOpen] = useState(false)
  const [grantRow, setGrantRow] = useState<ListRow | null>(null)
  const [selectedRole, setSelectedRole] = useState('')

  const refreshRoleList = useCallback(async () => {
    try {
      const res = await userRoleService.roleList()
      setAvailableRoles(res.data?.roles || [])
    } catch (e: unknown) {
      const err = e as { message?: string }
      notify(err?.message || 'Error', { type: 'error' })
    }
  }, [notify])

  const runSearch = useCallback(async () => {
    const email = emailInput.trim()
    const wallet = walletInput.trim()
    if (!email && !wallet) {
      notify(t('module.userRole.errors.emailOrWalletRequired'), { type: 'warning' })
      return
    }
    if (email && wallet) {
      notify(t('module.userRole.errors.emailOrWalletExclusive'), { type: 'warning' })
      return
    }

    const body = buildListRequest(emailInput, walletInput)
    if (!body) return

    setLoading(true)
    try {
      const res = await userRoleService.userRoleList(body)
      const next = rowsFromResponse(res.data as Record<string, unknown>, {
        userId: userIdInput,
        email: emailInput,
        wallet: walletInput,
      })
      setRows(next)
      if (!next.length) {
        notify(t('module.common.noData'), { type: 'info' })
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      notify(err?.response?.data?.message || err?.message || 'Error', { type: 'error' })
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [emailInput, walletInput, userIdInput, notify, t])

  const refreshAfterGrant = useCallback(async () => {
    const body = buildListRequest(emailInput, walletInput)
    if (!body) return
    setLoading(true)
    try {
      const res = await userRoleService.userRoleList(body)
      setRows(
        rowsFromResponse(res.data as Record<string, unknown>, {
          userId: userIdInput,
          email: emailInput,
          wallet: walletInput,
        })
      )
    } catch {
      /* 已在提交时提示 */
    } finally {
      setLoading(false)
    }
  }, [emailInput, walletInput, userIdInput])

  const openGrant = (row: ListRow) => {
    setGrantRow(row)
    setSelectedRole('')
    setGrantOpen(true)
  }

  const handleGrant = useCallback(async () => {
    if (!grantRow) return
    const idf = identifierFromRow(grantRow)
    if (!idf) {
      notify(t('module.userRole.errors.identifierRequired'), { type: 'warning' })
      return
    }
    if (!selectedRole) {
      notify(t('module.userRole.errors.roleRequired'), { type: 'warning' })
      return
    }
    setLoading(true)
    try {
      const res = await userRoleService.addUserRole({ ...idf, role: selectedRole })
      if (!res.data?.success) {
        notify(res.data?.message || t('module.userRole.messages.addFailed'), { type: 'error' })
        return
      }
      notify(t('module.userRole.messages.addSuccess'), { type: 'success' })
      setGrantOpen(false)
      setGrantRow(null)
      await refreshAfterGrant()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      notify(err?.response?.data?.message || err?.message || t('module.userRole.messages.addFailed'), {
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [grantRow, selectedRole, notify, t, refreshAfterGrant])

  useEffect(() => {
    refreshRoleList()
  }, [refreshRoleList])

  return (
    <PermissionGuard resource="user-role" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={t('resource.userRole')}
          links={[{ name: t('resource.dashboard') }, { name: t('resource.userRole') }]}
          sx={{ mb: { xs: 3, md: 5 } }}
        />

        <Card elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
          >
            <TextField
              fullWidth
              label={t('module.userRole.lookup.userId')}
              sx={{ flexBasis: '25%', flexShrink: 0 }}
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              placeholder={t('module.userRole.lookup.userIdOptional')}
            />
            <TextField
              fullWidth
              label={t('module.userRole.lookup.email')}
              sx={{ flexBasis: '25%', flexShrink: 0 }}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder={t('module.userRole.lookup.emailWalletHint')}
            />
            <TextField
              fullWidth
              label={t('module.userRole.lookup.walletAddress')}
              sx={{ flexBasis: '25%', flexShrink: 0 }}
              value={walletInput}
              onChange={(e) => setWalletInput(e.target.value)}
              placeholder={t('module.userRole.lookup.emailWalletHint')}
            />
            <Button
              variant="contained"
              label={t('resource.search')}
              onClick={runSearch}
              disabled={loading}
            >
              <Iconify icon="eva:search-fill" />
            </Button>
          </Stack>
          <Box sx={{ mt: 1, color: 'text.secondary', typography: 'caption' }}>
            {t('module.userRole.lookup.listHint')}
          </Box>
        </Card>

        <Card sx={{ pb: 2 }}>
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid rowClick={false} data={rows} isLoading={loading} bulkActionButtons={false}>
              <RaTextField
                source="user_id"
                label={t('module.userRole.table.userId')}
                emptyText="—"
              />
              <RaTextField source="email" label={t('module.userRole.lookup.email')} emptyText="—" />
              <RaTextField
                source="wallet_address"
                label={t('module.userRole.lookup.walletAddress')}
                emptyText="—"
              />
              <FunctionField
                label={t('module.userRole.table.roles')}
                render={(record: ListRow) =>
                  (record.roles || []).length ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {record.roles!.map((r) => (
                        <Box
                          key={r}
                          component="span"
                          sx={{
                            typography: 'caption',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                          }}
                        >
                          {r}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    '—'
                  )
                }
              />
              <FunctionField
                label={t('module.userRole.table.actions')}
                render={(record: ListRow) => (
                  <PermissionButton resource="user-role" action="create">
                    <Button
                      size="small"
                      label={t('module.userRole.grant.authorizeRole')}
                      onClick={() => openGrant(record)}
                      disabled={loading}
                    />
                  </PermissionButton>
                )}
              />
            </Datagrid>
          </Box>
        </Card>

        <Dialog
          open={grantOpen}
          onClose={() => !loading && setGrantOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('module.userRole.grant.authorizeRole')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>{t('module.userRole.grant.role')}</InputLabel>
                <Select
                  label={t('module.userRole.grant.role')}
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {availableRoles.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              label={t('ra.action.cancel')}
              onClick={() => setGrantOpen(false)}
              disabled={loading}
            />
            <Button label={t('ra.action.confirm')} onClick={handleGrant} disabled={loading} />
          </DialogActions>
        </Dialog>
      </DashboardContent>
    </PermissionGuard>
  );
}

export default View
